import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import { z } from 'zod';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { EDIT_PROFILE_PROFESSIONAL_URL } from '@/constants/links';
import { useToaster } from '@/hooks/useToaster';
import { clearUserAttributesCache } from '@/services/auth';
import {
  getCurrentUserV2QueryOptions,
  type CurrentUserV2 as UserV2,
} from '@/services/getCurrentUserV2';
import { getSubspecialtiesQueryOptions } from '@/services/getSubspecialties';
import { userIdAtom } from '@/services/globalState';
import { removeAvatar } from '@/services/removeAvatar';
import { updateUser } from '@/services/updateUser';
import { uploadAvatar } from '@/services/uploadAvatar';
import { segmentClient } from '@/util/analytics';

const defaultValues = {
  firstName: '',
  lastName: '',
  specialty: '',
  position: '',
  institution: '',
  city: '',
  state: '',
  cancerFocusIds: [],
  subspecialtyIds: [],
  fellowDirtyNudge: 0,
};

const profileProfessionalSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  specialty: z
    .union([z.string(), z.array(z.string())])
    .transform((val) => (Array.isArray(val) ? val[0] : val))
    .optional(),
  position: z.string().optional(),
  institution: z.string().optional(),
  city: z.string().optional(),
  state: z.string().min(1, 'State is required'),
  cancerFocusIds: z.array(z.number()).optional(),
  subspecialtyIds: z.array(z.number()).optional(),
  fellowDirtyNudge: z.number().optional(),
});

function getSpecialtyWithFellow(specialty: string, checked: boolean): string {
  if (checked) {
    if (!specialty) {
      return 'Fellow';
    }

    if (specialty.trim().endsWith(', Fellow')) {
      return specialty;
    }

    return specialty.endsWith(',') ? specialty + ' Fellow' : specialty + ', Fellow';
  }

  if (specialty.trim().endsWith(', Fellow')) {
    return specialty.slice(0, -8);
  }

  if (specialty.trim().endsWith('Fellow')) {
    return specialty.slice(0, -6);
  }

  return specialty;
}

export function useEditProfessional() {
  const router = useRouter();

  const { id } = useLocalSearchParams();

  const userId = Array.isArray(id) ? id[0] : id;

  const queryClient = useQueryClient();

  const toaster = useToaster();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showRemoveAvatarModal, setShowRemoveAvatarModal] = useState(false);

  const userQueryKey = getCurrentUserV2QueryOptions(userId).queryKey;

  const { data: userData, isLoading } = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    queryKey: userQueryKey,
  });

  const { mutateAsync: updateUserMutation } = useMutation({ mutationFn: updateUser });

  const { mutate: uploadAvatarMutation, isPending: isUploadingAvatar } = useMutation({
    mutationFn: uploadAvatar,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(userQueryKey, updatedUser as UserV2);

      toaster.showSuccess({ title: 'Avatar updated successfully' });
    },
    onError: (error) => {
      toaster.showError({
        title: 'Failed to upload avatar',
        message: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    },
  });

  const { mutate: removeAvatarMutation, isPending: isRemovingAvatar } = useMutation({
    mutationFn: removeAvatar,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(userQueryKey, updatedUser as UserV2);

      toaster.showSuccess({ title: 'Avatar removed successfully' });

      setShowRemoveAvatarModal(false);
    },
    onError: (error) => {
      toaster.showError({
        title: 'Failed to remove avatar',
        message: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    },
  });

  const subspecialtiesQuery = useQuery(getSubspecialtiesQueryOptions());

  const form = useForm({
    defaultValues,
    resolver: zodResolver(profileProfessionalSchema),
    mode: 'onChange',
  });

  const [isFellow, setIsFellow] = useState(userData?.specialty?.trim().endsWith('Fellow') || false);

  useEffect(() => {
    if (userData) {
      form.reset({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        specialty: userData.specialty?.trim().endsWith('Fellow')
          ? userData.specialty?.replace(/,?\s*Fellow$/, '').trim()
          : userData.specialty || '',
        position: userData.position || '',
        institution: userData.institution || '',
        city: userData.city || '',
        state: userData.state || '',
        cancerFocusIds: userData.cancerFocusIds?.map((f: any) => f.id) || [],
        subspecialtyIds: userData.subspecialties?.map((s: any) => s.id) || [],
        fellowDirtyNudge: 0,
      });

      setIsFellow(userData?.specialty?.trim().endsWith('Fellow') || false);
    }
  }, [userData, form.reset]);

  const onSubmit = async () => {
    // CASL rules aren't JSON-serializable analytics props and aren't useful here; strip them.
    const { rules: _rules, ...previousForAnalytics } = userData ?? {};

    try {
      Keyboard.dismiss();

      const values = form.getValues();

      const specialty = getSpecialtyWithFellow(values.specialty || '', isFellow);

      await updateUserMutation({
        ...values,
        specialty,
      });

      form.reset(form.getValues(), { keepValues: true, keepDirty: false });

      queryClient.invalidateQueries();

      clearUserAttributesCache();

      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, {
        section: 'professional',
        previous: previousForAnalytics,
        current: form.getValues(),
      });

      toaster.showSuccess({ title: 'Profile Updated' });

      if (router.canGoBack?.()) {
        router.back();
      } else {
        router.replace(`/profile/${userId}`);
      }
    } catch (error) {
      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_FAILED, {
        section: 'professional',
        previous: previousForAnalytics,
        current: form.getValues(),
      });

      toaster.showError({
        title: 'Failed to save profile',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      uploadAvatarMutation({ avatar: file });
    }
  };

  const currentUserId = useAtomValue(userIdAtom);

  const isOwnProfile = Number(userId) === Number(currentUserId);

  const handleAvatarClick = () => {
    if (!isOwnProfile) {
      return;
    }

    if (isWeb) {
      fileInputRef.current?.click();
    } else {
      toaster.showInfo({
        title: 'Coming soon to mobile',
        message: `Please update your profile photo at ${EDIT_PROFILE_PROFESSIONAL_URL(userId)}`,
      });
    }
  };

  const handleRemoveAvatarRequest = () => {
    setShowRemoveAvatarModal(true);
  };

  const handleRemoveAvatar = () => {
    removeAvatarMutation();
  };

  return {
    form,
    isLoading,
    userData,
    subspecialtiesQuery,
    isFellow,
    setIsFellow,
    isOwnProfile,
    fileInputRef,
    handleFileChange,
    onSubmit,
    userId,
    handleAvatarClick,
    handleRemoveAvatar,
    showRemoveAvatarModal,
    setShowRemoveAvatarModal,
    handleRemoveAvatarRequest,
    isRemovingAvatar,
  };
}
